// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import { ActionSheetV2, LgPadding } from '@alto/design-system';
import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useOrderPricing } from '~shared/queries';
import { type OrderPricingQueryData } from '~shared/queries/useOrderPricing';
import { type Prescription } from '~shared/types';
import { useTrackPriceBreakdownViewed } from '../hooks/useTrackPriceBreakdownViewed';
import { PaymentBreakdown, type PricingBreakdownPrescription } from './PaymentBreakdown';

type Props = {
  readonly origin: string;
  readonly prescriptions: Prescription[] | PricingBreakdownPrescription[];
  readonly forSinglePrescription?: boolean;
  readonly shipmentID?: number | null;
  readonly bundlingOptionShipmentID?: number;
};

export const PricingBreakdownActionSheet = ({
  forSinglePrescription = false,
  origin,
  prescriptions,
  shipmentID,
  bundlingOptionShipmentID,
}: Props) => {
  const { orderPricing }: { orderPricing: OrderPricingQueryData | undefined } = useOrderPricing(
    bundlingOptionShipmentID !== undefined ? { bundlingOptionShipmentID } : { shipmentID: shipmentID ?? undefined },
  );

  // In split shipment scenario, only show prescriptions that belong to the current order (orderPricing is scoped to current order)
  const itemPricing = orderPricing?.item_pricing;
  const prescriptionsForCurrentOrder = ((): Prescription[] | PricingBreakdownPrescription[] => {
    if (!itemPricing) return prescriptions;
    const idsInOrder = new Set(Object.keys(itemPricing).map(Number));
    return prescriptions.filter((p) => idsInOrder.has(p.id)) as Prescription[] | PricingBreakdownPrescription[];
  })();

  // key to include in analytics event to enable querying 'item viewed' events for given 'payment breakdown viewed' event.
  // Regenerate the id whenever the breakdown contents change (adjust state during render — no Effect needed).
  const [eventUuid, setEventUuid] = useState(() => uuid());
  const [prevPrescriptions, setPrevPrescriptions] = useState(prescriptionsForCurrentOrder);
  if (prevPrescriptions !== prescriptionsForCurrentOrder) {
    setPrevPrescriptions(prescriptionsForCurrentOrder);
    setEventUuid(uuid());
  }

  useTrackPriceBreakdownViewed({
    lineItems: orderPricing?.line_items,
    numberOfRxs: prescriptionsForCurrentOrder.length,
    origin,
    breakdownKey: eventUuid,
  });

  return (
    <ActionSheetV2 analyticsName="pricing breakdown">
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <PaymentBreakdown
          orderPricing={orderPricing}
          prescriptions={prescriptionsForCurrentOrder}
          forSinglePrescription={forSinglePrescription}
          origin={origin}
          eventUuid={eventUuid}
        />
      </LgPadding>
    </ActionSheetV2>
  );
};
